import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, CheckSquare, Phone, Search, User, UserPlus, X } from 'lucide-react'
import { listContactsPage } from '@/api/contacts'
import { searchDeals } from '@/api/deals'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import NewDealModal from '@/features/deals/NewDealModal'
import NewTaskModal from '@/features/tasks/NewTaskModal'
import { cn } from '@/lib/cn'

type Item = { key: string; icon: React.ReactNode; label: string; detail?: string; run: () => void }

/**
 * Find anything from anywhere: clients by name, phone or email, deals by address — plus "create"
 * shortcuts. Opened from the search box at the top of the sidebar, or Ctrl/⌘+K anywhere.
 */
export default function GlobalSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [creating, setCreating] = useState<'deal' | 'task' | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const query = useDebouncedValue(q.trim(), 250)

  // The contacts table search: name, email, and phone ignoring spaces/brackets (0412 345 678).
  const contacts = useQuery({
    queryKey: ['search', 'contacts', query],
    queryFn: () => listContactsPage({ q: query, size: 5 }),
    enabled: query.length >= 2,
  })
  const deals = useQuery({
    queryKey: ['search', 'deals', query],
    queryFn: () => searchDeals(query, 5),
    enabled: query.length >= 2,
  })

  const go = (to: string) => {
    onClose()
    navigate(to)
  }
  const contactItems: Item[] = (query.length >= 2 ? contacts.data?.items ?? [] : []).map((c) => ({
    key: `c-${c.id}`,
    icon: <User size={15} />,
    label: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact',
    detail: [c.mobile || c.phone, c.email].filter(Boolean).join(' · '),
    run: () => go(`/contacts/${c.id}`),
  }))
  const dealItems: Item[] = (query.length >= 2 ? deals.data ?? [] : []).map((d) => ({
    key: `d-${d.id}`,
    icon: <Building2 size={15} />,
    label: d.title,
    run: () => go(`/deals/${d.id}`),
  }))
  const createItems: Item[] = [
    { key: 'new-contact', icon: <UserPlus size={15} />, label: 'New contact', run: () => go('/contacts?add=1') },
    { key: 'new-deal', icon: <Building2 size={15} />, label: 'New deal', run: () => setCreating('deal') },
    { key: 'new-task', icon: <CheckSquare size={15} />, label: 'New task', run: () => setCreating('task') },
  ]
  const groups = [
    { title: 'Clients', items: contactItems },
    { title: 'Deals', items: dealItems },
    { title: 'Create', items: createItems },
  ].filter((g) => g.items.length > 0)
  const flat = groups.flatMap((g) => g.items)
  const indexOf = new Map(flat.map((it, i) => [it.key, i]))
  const loading = query.length >= 2 && (contacts.isFetching || deals.isFetching)
  const nothing = query.length >= 2 && !loading && contactItems.length === 0 && dealItems.length === 0

  useEffect(() => inputRef.current?.focus(), [])

  if (creating === 'deal') {
    return <NewDealModal onClose={onClose} onCreated={(d) => go(`/deals/${d.id}`)} />
  }
  if (creating === 'task') return <NewTaskModal onClose={onClose} />

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Search" className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 border-b border-gray-100">
          <Search size={16} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose()
              else if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 1, flat.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 1, 0))
              } else if (e.key === 'Enter') {
                flat[active]?.run()
              }
            }}
            placeholder="Search clients by name, phone or email, or deals by address…"
            aria-label="Search"
            className="flex-1 min-w-0 py-3.5 text-sm focus:outline-none"
          />
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {q.trim().length > 0 && q.trim().length < 2 && <p className="px-3 py-2 text-xs text-gray-400">Keep typing…</p>}
          {loading && <p className="px-3 py-2 text-xs text-gray-400">Searching…</p>}
          {nothing && <p className="px-3 py-2 text-sm text-gray-500">No clients or deals match “{query}”.</p>}
          {groups.map((g) => (
            <div key={g.title} className="mb-1">
              <div className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-gray-400">{g.title}</div>
              {g.items.map((it) => {
                const i = indexOf.get(it.key) ?? 0
                return (
                  <button
                    key={it.key}
                    type="button"
                    onClick={it.run}
                    onMouseEnter={() => setActive(i)}
                    className={cn('w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left', i === active ? 'bg-brand-primary/10' : 'hover:bg-gray-50')}
                  >
                    <span className="text-gray-400 shrink-0">{it.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-brand-darkest truncate">{it.label}</span>
                      {it.detail && (
                        <span className="flex items-center gap-1 text-xs text-gray-500 truncate">
                          {it.detail.match(/\d/) && <Phone size={10} className="shrink-0" />} {it.detail}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400">
          <span>↑↓ to move</span><span>Enter to open</span><span>Esc to close</span>
        </div>
      </div>
    </div>
  )
}
